/**
 * BehaviorProbeCard — Nikki's Behaviour Check-in.
 *
 * The follow-through counterpart to the Mind & Body Check-in: when the user
 * missed a session / meals / a check-in, Nikki asks ONE warm question and the
 * user taps the reason. Each chip is pre-classified to a COM-B barrier
 * (Time = opportunity, Body = capability, Motivation = motivation) and fires
 * the `barrier_report` intent so the backend stores it + adapts today's plan.
 */
import React from 'react';
import { Compass, Clock, HeartPulse, BatteryLow } from 'lucide-react';

const ICONS = { opportunity: Clock, capability: HeartPulse, motivation: BatteryLow };

const BehaviorProbeCard = ({ data, onAction }) => {
  const { behavior, question, options = [] } = data || {};
  return (
    <div
      className="rounded-sm border border-amber-400/30 bg-gradient-to-br from-zinc-900/70 to-zinc-900/20 p-4 space-y-3"
      data-testid="behavior-probe-card"
    >
      <div className="flex items-center gap-2">
        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-400/15 border border-amber-400/40">
          <Compass className="w-3.5 h-3.5 text-amber-300" />
        </span>
        <span className="text-[10px] uppercase tracking-[0.18em] text-amber-300 font-secondary font-bold">
          Behaviour Check-in
        </span>
      </div>

      <p className="text-sm text-zinc-100 leading-relaxed" data-testid="behavior-probe-question">
        {question}
      </p>

      <div className="flex flex-col gap-2">
        {options.map(opt => {
          const Icon = ICONS[opt.id] || Compass;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onAction?.({ type: 'send', payload: { intent: 'barrier_report', behavior, barrier: opt.id } })}
              className="group flex items-center gap-2.5 px-3 py-2.5 rounded-sm border border-zinc-700 bg-zinc-900/60 hover:border-amber-400 hover:bg-amber-400/10 hover:text-amber-200 transition-all text-sm text-left"
              data-testid={`behavior-probe-opt-${opt.id}`}
            >
              <Icon className="w-4 h-4 shrink-0 text-zinc-400 group-hover:text-amber-300" />
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => onAction?.({ type: 'send', payload: { intent: 'barrier_report', behavior, barrier: 'skip' } })}
        className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
        data-testid="behavior-probe-skip"
      >
        Skip for now
      </button>
    </div>
  );
};

export default BehaviorProbeCard;
